export const Feature = (props: { children: React.ReactNode }) => {
  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 30px;
      `}
    >
      {props.children}
    </div>
  );
};

export const Descriptions = (props: { children: React.ReactNode }) => {
  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 30px;

        @media (min-width: 960px) {
          flex-direction: row;
          gap: 38px;
        }
      `}
    >
      {props.children}
    </div>
  );
};

export const Heading = (props: { children: React.ReactNode }) => {
  return (
    <h4
      css={`
        margin: 0;
        padding: 0;
        font-size: 32px;
        font-weight: 700;
        font-family: "Pretendard", sans-serif;
        line-height: 36px;
        letter-spacing: -0.384px;
        text-align: center;

        @media (min-width: 960px) {
          margin: 0 0 20px 0;
          line-height: 72px;
          font-size: 52px;
          font-weight: 700;
          letter-spacing: -0.624px;
        }
      `}
    >
      {props.children}
    </h4>
  );
};

// Rounded frame with the subtle drop shadow shared by every feature
// screenshot. Image assets are flat (no shadow baked in), so the shadow
// lives here once and is reused across the main and description images.
export const ImageFrame = (props: { children: React.ReactNode }) => {
  return (
    <div
      css={`
        border-radius: 20px;
        box-shadow: 0px 2.189px 10.947px 0px rgba(0, 0, 0, 0.05) !important;
        display: flex;
      `}
    >
      {props.children}
    </div>
  );
};

export const FeatureCard = (props: {
  title: string;
  description: string;
  image: React.ReactNode;
  color: string;
  link?: { href: string; label: string };
}) => {
  return (
    <div
      css={`
        overflow: hidden;
        border: 1px solid #e4e4e7;
        border-radius: 20px;
        background: ${props.color};
        display: flex;
        flex-direction: column;
        gap: 12px;

        @media (min-width: 960px) {
          flex-direction: row;
          gap: 70px;
        }
      `}
    >
      <div
        css={`
          padding: 30px 0 20px 30px;
          display: flex;
          flex-direction: column;
          gap: 20px;
          justify-content: center;

          @media (min-width: 960px) {
            padding-left: 90px;
            // max-width: 490px;
            flex: 1;
            gap: 39px;
          }
        `}
      >
        <div
          css={`
            line-height: 36px;
            font-size: 30px;
            font-weight: 500;
            font-family: "Pretendard", sans-serif;
            letter-spacing: -0.36px;

            @media (min-width: 960px) {
              display: flex;
              flex-direction: column;
              justify-content: center;
              line-height: 48px;
              font-size: 48px;
              font-weight: 600;
              letter-spacing: -0.576px;
            }
          `}
        >
          {props.title}
        </div>
        <div
          css={`
            font-size: 18px;
            font-weight: 400;
            font-family: "Pretendard", sans-serif;
            line-height: 24px;

            @media (min-width: 960px) {
              font-size: 24px;
              line-height: 33.986px;
            }
          `}
        >
          {props.description}
        </div>

        {props.link && (
          <a
            href={props.link.href}
            css={`
              display: inline-flex;
              align-items: center;
              gap: 8px;
              width: fit-content;
              color: #325aff;
              font-size: 18px;
              line-height: 24px;
              font-weight: 600;
              font-family: "Pretendard", sans-serif;
              text-decoration: none;
              transition:
                gap 120ms cubic-bezier(0.5, 1, 0.89, 1),
                opacity 120ms cubic-bezier(0.5, 1, 0.89, 1);

              :hover {
                gap: 12px;
                opacity: 0.85;
              }

              @media (min-width: 960px) {
                font-size: 20px;
                line-height: 28px;
              }
            `}
          >
            {props.link.label}
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M4.167 10h11.666M10 4.167 15.833 10 10 15.833"
                stroke="#325aff"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        )}
      </div>

      <div
        css={`
          padding: 0 0 0 30px;
          display: flex;

          @media (min-width: 960px) {
            flex: 1.65;
            padding: 60px 0 0 0;
          }
        `}
      >
        <ImageFrame>{props.image}</ImageFrame>
      </div>
    </div>
  );
};

export const DescriptionCard = (props: { children: React.ReactNode }) => {
  return (
    <div
      css={`
        flex: 1;
        overflow: hidden;
        border: 1px solid #e4e4e7;
        border-radius: 20px;
        background-color: #f8fafc;
      `}
    >
      {props.children}
    </div>
  );
};

export const DescriptionTitle = (props: { title: React.ReactNode }) => {
  return (
    <div
      css={`
        padding: 30px 15px 0;
        line-height: 28px;
        font-size: 20px;
        font-weight: 600;
        font-family: "Pretendard", sans-serif;
        letter-spacing: -0.24px;
        color: #09090b;
        text-align: center;

        @media (min-width: 960px) {
          line-height: 48px;
          font-size: 30px;
          letter-spacing: -0.36px;
        }
      `}
    >
      {props.title}
    </div>
  );
};
